import { useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { ErrorToast, SuccessToast } from "@ciq/shared/utils/toast"
import { PANE_NAME_MAP } from "../../components/modals/settings/view"

/**
 * @typedef {Object} OAuthCallbackParams
 * @property {boolean} success - Whether the OAuth flow succeeded
 * @property {string|null} integrationId - The integration UUID
 * @property {string|null} message - Success or error message from integrationsapi
 */

/**
 * Parses OAuth callback query parameters from integrationsapi redirect.
 *
 * @param {URLSearchParams} params - URL search parameters
 * @returns {OAuthCallbackParams} Parsed callback parameters
 */
export function parseOAuthCallbackParams(params) {
  return {
    success: params.get("success") === "true",
    integrationId: params.get("integration_id") || null,
    message: params.get("message") || null,
  }
}

/**
 * OAuth callback landing page.
 * Handles the redirect from integrationsapi after vendor OAuth completes.
 * Parses query params, shows a toast notification, and navigates to the main view.
 * If the URL contains a settings param (e.g., ?settings=integrations), it is
 * forwarded so the settings modal deep-link mechanism opens the correct pane.
 *
 * @returns {null} Renders nothing — redirects immediately
 */
export default function OAuthCallback() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  useEffect(() => {
    // Only process if we have actual OAuth callback params (success is always present in a real callback)
    if (!searchParams.has("success")) {
      navigate("/", { replace: true })
      return
    }

    const { success, message } = parseOAuthCallbackParams(searchParams)

    if (success) {
      SuccessToast(message || "Integration connected successfully")
    } else {
      ErrorToast(message || "Unable to complete integration")
    }

    // Forward the settings param if present so the deep-link mechanism
    // in SettingsModal opens to the correct pane (e.g., integrations).
    // Validate against known pane keys to prevent URL injection.
    const rawSettings = searchParams.get("settings")
    const settingsParam = rawSettings && PANE_NAME_MAP[rawSettings] ? rawSettings : null
    const target = settingsParam ? `/?settings=${settingsParam}` : "/"

    navigate(target, { replace: true })
  }, [searchParams, navigate])

  return null
}
